import { getViewSettingsSafely, ViewIdentifiable} from "../../models/common/ViewSettings";
import {ApiReturnType} from "../../models/common/ApiReturnType";
import {Employee, EmployeeModule, EmployeeRequest} from "../../models/users/Employee";
import { ofType, StateObservable} from "redux-observable";
import {AppState} from "../../reducers/RootReducer";
import {
    FETCH_USER_VIEW_SETTINGS_SUCCESS,
    HANDLE_SEARCH_TRIGGERED, sendUserToPage, VIEW_SORT_CHANGE,
    ViewActionTypes
} from "../common/UserViewSettingsActions";
import {catchError, filter, map, mergeMap, switchMap} from "rxjs/operators";
import {getUrlParamsFromViewSettings, RouteConfig, RouteMethods} from "../../config/RouteConfig";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {ajax} from "rxjs/ajax";
import { Observable, of } from 'rxjs';
import {eventNotificationShow} from "../common/EventNotificationActions";
import {EVENT_NOTIFICATION_TYPE} from "../../helpers/EventNotificationType";
import {ErrorAction} from "../../models/common/ActionTypes";
import {currentLanguageFromStore} from "../../config/LanguageConfig";
import {createErrorResponse} from "../../config/languages/definitions/AjaxResponses";
import {dateAsTimestamp, nullableDateAsTimestamp} from "../../helpers/DateTimeHelpers";
import {fetchUserGroupsByUser} from "../user-groups/UserGroupsActions";

export type EmployeeActionTypes = EmployeeViewActionTypes
    | FetchEmployeeActionTypes
    | CreateEmployeeActionTypes
    | UpdateEmployeeActionTypes
    | CheckEmailUniquenessActionTypes
    | DeleteEmployeeActionTypes
    | RestoreEmployeeActionTypes

/**
 * FETCHING EMPLOYEES
 */
export interface FetchEmployeesAction extends ViewIdentifiable {
    type: typeof FETCH_EMPLOYEES
}
export interface FetchEmployeesSuccessAction extends ViewIdentifiable {
    type: typeof FETCH_EMPLOYEES_SUCCESS
    payload: ApiReturnType< Employee[] >
}
export interface FetchEmployeesErrorAction extends ViewIdentifiable {
    type: typeof FETCH_EMPLOYEES_ERROR
    error: Error
}
export type EmployeeViewActionTypes = FetchEmployeesAction | FetchEmployeesErrorAction | FetchEmployeesSuccessAction;
type EmployeeViewSettingsActionTypes = EmployeeViewActionTypes | ViewActionTypes;

export const FETCH_EMPLOYEES = "FETCH_EMPLOYEES";
export const FETCH_EMPLOYEES_SUCCESS = "FETCH_EMPLOYEES_SUCCESS";
export const FETCH_EMPLOYEES_ERROR = "FETCH_EMPLOYEES_ERROR";

export const fetchEmployees = ( viewIdentifier: string ): FetchEmployeesAction => ({ type: FETCH_EMPLOYEES, viewIdentifier });
export const fetchEmployeesSuccess = ( viewIdentifier: string, payload: ApiReturnType< Employee[] > ): FetchEmployeesSuccessAction =>
    ({ type: FETCH_EMPLOYEES_SUCCESS, payload, viewIdentifier });
export const fetchEmployeesError = (viewIdentifier: string, error: Error ): FetchEmployeesErrorAction =>
    ({ type: FETCH_EMPLOYEES_ERROR, viewIdentifier, error });

export const fetchEmployeesEpic = (
    action$: Observable< EmployeeViewSettingsActionTypes >,
    store$: StateObservable< AppState >
) => action$.pipe(
        ofType( VIEW_SORT_CHANGE, FETCH_EMPLOYEES, HANDLE_SEARCH_TRIGGERED, FETCH_USER_VIEW_SETTINGS_SUCCESS ),
        filter( action  => action.viewIdentifier === RouteConfig.employees.viewIdentifier ),
        mergeMap( action => {
            const userViewSettings = getViewSettingsSafely( action.viewIdentifier, store$ );
            const path = `${ APP_CONFIG.baseWeb }employees/${  getUrlParamsFromViewSettings( userViewSettings) }`;

            return ajax( APP_CONFIG.getRequestObject( path ) ).pipe(
                map( response => fetchEmployeesSuccess(
                    action.viewIdentifier,
                    response.response as ApiReturnType< Employee[] >
                ) ),
                catchError( error =>
                    of(
                        fetchEmployeesError( action.viewIdentifier, error ),
                        eventNotificationShow(
                            EVENT_NOTIFICATION_TYPE.customAjaxError,
                            "Mitarbeiter konnten nicht geladen werden!"
                        )
                    )
                )
            );
        })
    );

/**
 * FETCH SINGLE EMPLOYEE
 */
export interface FetchEmployeeAction {
    type: typeof FETCH_EMPLOYEE
    id: number
}
export interface FetchEmployeeSuccessAction {
    type: typeof FETCH_EMPLOYEE_SUCCESS
    id: number
    payload: Employee
}
export interface FetchEmployeeErrorAction {
    type: typeof FETCH_EMPLOYEE_ERROR
    error: Error
}
export type FetchEmployeeActionTypes = FetchEmployeeAction | FetchEmployeeSuccessAction | FetchEmployeeErrorAction;

export const FETCH_EMPLOYEE = "FETCH_EMPLOYEE";
export const FETCH_EMPLOYEE_SUCCESS = "FETCH_EMPLOYEE_SUCCESS";
export const FETCH_EMPLOYEE_ERROR = "FETCH_EMPLOYEE_ERROR";

export const fetchEmployee = ( id: number ): FetchEmployeeAction => ({ type: FETCH_EMPLOYEE, id });
export const fetchEmployeeSuccess = ( id: number, payload: Employee ): FetchEmployeeSuccessAction => ({ type: FETCH_EMPLOYEE_SUCCESS, id, payload });
export const fetchEmployeeError = ( error: Error ): FetchEmployeeErrorAction => ({ type: FETCH_EMPLOYEE_ERROR, error });

export const fetchEmployeeEpic = ( action$: Observable< FetchEmployeeAction > ) =>
    action$.pipe(
        ofType( FETCH_EMPLOYEE ),
        mergeMap( action => {

            const path = `${ APP_CONFIG.baseWeb }employees/${ action.id }`;

            return ajax( APP_CONFIG.getRequestObject( path ) ).pipe(
                map( response => fetchEmployeeSuccess( action.id, response.response as Employee ) ),
                catchError( error =>
                    of(
                        fetchEmployeeError( error ),
                        eventNotificationShow(
                            EVENT_NOTIFICATION_TYPE.customAjaxError,
                            "Mitarbeiter konnte nicht geladen werden!"
                        )
                    )
                )
            )
        })
    );


/**
 * RESTORE EMPLOYEE
 */
export interface RestoreEmployeeAction {
    type: typeof RESTORE_EMPLOYEE
    id: number
}
export interface RestoreEmployeeSuccessAction {
    type: typeof RESTORE_EMPLOYEE_SUCCESS
    id: number
}
export interface RestoreEmployeeErrorAction {
    type: typeof RESTORE_EMPLOYEE_ERROR
    error: Error
}

export type RestoreEmployeeActionTypes = RestoreEmployeeAction | RestoreEmployeeErrorAction | RestoreEmployeeSuccessAction

export const RESTORE_EMPLOYEE = "RESTORE_EMPLOYEE";
export const RESTORE_EMPLOYEE_SUCCESS = "RESTORE_EMPLOYEE_SUCCESS";
export const RESTORE_EMPLOYEE_ERROR = "RESTORE_EMPLOYEE_ERROR";

export const restoreEmployee = ( id: number ): RestoreEmployeeAction => ({ type: RESTORE_EMPLOYEE, id });
export const restoreEmployeeSuccess = ( id: number ): RestoreEmployeeSuccessAction => ( { type: RESTORE_EMPLOYEE_SUCCESS, id });
export const restoreEmployeeError = ( error: Error ): RestoreEmployeeErrorAction => ( { type: RESTORE_EMPLOYEE_ERROR, error } );

export const restoreEmployeeEpic = (
    action$: Observable< RestoreEmployeeAction >,
    store$: StateObservable<AppState>
) =>
    action$.pipe(
        ofType( RESTORE_EMPLOYEE ),
        switchMap( action => {
            const path = `${ APP_CONFIG.baseWeb }employees/${action.id}`;
            const language = currentLanguageFromStore( store$ );
            return ajax( APP_CONFIG.getRequestObject( path, RouteMethods.patch ) ).pipe(
                mergeMap( _ => of(
                    restoreEmployeeSuccess( action.id ),
                    eventNotificationShow( EVENT_NOTIFICATION_TYPE.ajaxSuccess, language.ajaxResponse.restoreSuccess )
                ) ),
                catchError( error => of(
                    restoreEmployeeError( error ),
                    eventNotificationShow( EVENT_NOTIFICATION_TYPE.customAjaxError, createErrorResponse( language, error.status, "employee", "restoreAction"))
                ) )
            )
        } )
    );

/**
 * DELETE EMPLOYEE
 */
export interface DeleteEmployeeAction {
    type: typeof DELETE_EMPLOYEE
    id: number
}
export interface DeleteEmployeeSuccessAction {
    type: typeof DELETE_EMPLOYEE_SUCCESS
    id: number
}
export interface DeleteEmployeeErrorAction {
    type: typeof DELETE_EMPLOYEE_ERROR
    error: Error
}

export type DeleteEmployeeActionTypes = DeleteEmployeeAction | DeleteEmployeeErrorAction | DeleteEmployeeSuccessAction

export const DELETE_EMPLOYEE = "DELETE_EMPLOYEE";
export const DELETE_EMPLOYEE_SUCCESS = "DELETE_EMPLOYEE_SUCCESS";
export const DELETE_EMPLOYEE_ERROR = "DELETE_EMPLOYEE_ERROR";

export const deleteEmployee = ( id: number  ): DeleteEmployeeAction => ({ type: DELETE_EMPLOYEE, id });
export const deleteEmployeeSuccess = (id: number  ): DeleteEmployeeSuccessAction => ({ type: DELETE_EMPLOYEE_SUCCESS, id });
export const deleteEmployeeError = ( error: Error ): DeleteEmployeeErrorAction => ({ type: DELETE_EMPLOYEE_ERROR, error });

export const deleteEmployeeEpic = (
    action$: Observable<DeleteEmployeeAction>,
    store$: StateObservable<AppState>
) =>
    action$.pipe(
        ofType( DELETE_EMPLOYEE ),
        switchMap( action => {
                const language = currentLanguageFromStore( store$ );
                const path = `${ APP_CONFIG.baseWeb }employees/${action.id}`;

                return ajax( APP_CONFIG.getRequestObject( path, RouteMethods.delete ) ).pipe(
                    mergeMap( _ => of(
                        deleteEmployeeSuccess( action.id ),
                        fetchEmployees( EmployeeModule.identifier ),
                        eventNotificationShow( EVENT_NOTIFICATION_TYPE.ajaxSuccess, language.ajaxResponse.deleteSuccess )
                    )),
                    catchError( error => of(
                        deleteEmployeeError(error),
                        eventNotificationShow( EVENT_NOTIFICATION_TYPE.customAjaxError, createErrorResponse( language, error.status, "employee", "deleteAction"))
                    )
                    ));

            }
        )
    );

/**
 * CREATE NEW EMPLOYEE
 */
export interface CreateEmployeeAction {
    type: typeof CREATE_EMPLOYEE
    employee: EmployeeRequest
}
export interface CreateEmployeeSuccessAction {
    type: typeof CREATE_EMPLOYEE_SUCCESS
    employee: Employee
}
export interface CreateEmployeeErrorAction extends ErrorAction< typeof CREATE_EMPLOYEE_ERROR >{
    employee: EmployeeRequest
}

export type CreateEmployeeActionTypes = CreateEmployeeAction | CreateEmployeeErrorAction | CreateEmployeeSuccessAction

export const CREATE_EMPLOYEE = "CREATE_EMPLOYEE";
export const CREATE_EMPLOYEE_SUCCESS = "CREATE_EMPLOYEE_SUCCESS";
export const CREATE_EMPLOYEE_ERROR = "CREATE_EMPLOYEE_ERROR";

export const createEmployee = ( employee: EmployeeRequest ): CreateEmployeeAction => ({ type: CREATE_EMPLOYEE, employee });
export const createEmployeeSuccess = (employee: Employee): CreateEmployeeSuccessAction => ({ type: CREATE_EMPLOYEE_SUCCESS, employee });
export const createEmployeeError = (employee: EmployeeRequest, error: Error): CreateEmployeeErrorAction => ({ type: CREATE_EMPLOYEE_ERROR, employee, error });

export const createEmployeeEpic = (
    action$: Observable<CreateEmployeeAction>,
    store$: StateObservable<AppState>
) =>
    action$.pipe(
        ofType( CREATE_EMPLOYEE ),
        switchMap( action => {
            const path = `${APP_CONFIG.baseWeb}employees/`;
            const language = currentLanguageFromStore( store$ );
            let daysOfWork = {};

            if( action.employee.contract !== null ) {

                for( const index in action.employee.contract.daysOfWork ) {
                    daysOfWork = {
                        ...daysOfWork,
                        [index]: {
                            ...action.employee.contract?.daysOfWork[index],
                            start: action.employee.contract?.daysOfWork[index].start.getTime() ?? null,
                            end: action.employee.contract?.daysOfWork[index].end.getTime() ?? null,
                            //dayOfWeek: Weekday[ action.employee.contract?.daysOfWork[ index ].dayOfWeek ] ?? 0
                        }
                   }
                }
            }


            return ajax( APP_CONFIG.getRequestObject( path, RouteMethods.post, {
                ...action.employee,
                birthday: action.employee.birthday.getTime(),
                contract: {
                    ...action.employee.contract,
                    start: action.employee.contract?.start?.getTime() ?? null,
                    end: action.employee.contract?.end?.getTime() ?? null,
                    daysOfWork: daysOfWork
                },
                qualifications: action.employee.qualifications?.map( quali => {
                    return {
                        ...quali,
                        lastRefresh: quali.lastRefresh.getTime(),
                        nextRefresh: quali.nextRefresh?.getTime() ?? null
                    };
                }) ?? []
            }) ).pipe(
                mergeMap( response => of(
                    createEmployeeSuccess( response.response as Employee ),
                    eventNotificationShow( EVENT_NOTIFICATION_TYPE.ajaxSuccess ),
                    fetchEmployees( EmployeeModule.identifier ),
                    sendUserToPage( RouteConfig.employees.routes.main.path , EmployeeModule.identifier )
                )),
                catchError( error => of(
                    createEmployeeError( action.employee, error ),
                    eventNotificationShow( EVENT_NOTIFICATION_TYPE.customAjaxError, `Mitarbeiter konnte nicht gespeichert werden.` )
                ) )
            );
        })
    );

/**
 * UPDATE NEW EMPLOYEE
 */
export interface UpdateEmployeeAction {
    type: typeof UPDATE_EMPLOYEE
    employee: EmployeeRequest
}
export interface UpdateEmployeeSuccessAction {
    type: typeof UPDATE_EMPLOYEE_SUCCESS
    employee: Employee
}
export interface UpdateEmployeeErrorAction extends ErrorAction< typeof UPDATE_EMPLOYEE_ERROR >{
    employee: EmployeeRequest
}

export type UpdateEmployeeActionTypes = UpdateEmployeeAction
    | UpdateEmployeeErrorAction
    | UpdateEmployeeSuccessAction

export const UPDATE_EMPLOYEE = "UPDATE_EMPLOYEE";
export const UPDATE_EMPLOYEE_SUCCESS = "UPDATE_EMPLOYEE_SUCCESS";
export const UPDATE_EMPLOYEE_ERROR = "UPDATE_EMPLOYEE_ERROR";

export const updateEmployee = ( employee: EmployeeRequest ): UpdateEmployeeAction => ({ type: UPDATE_EMPLOYEE, employee });
export const updateEmployeeSuccess = (employee: Employee): UpdateEmployeeSuccessAction => ({ type: UPDATE_EMPLOYEE_SUCCESS, employee });
export const updateEmployeeError = (employee: EmployeeRequest, error: Error): UpdateEmployeeErrorAction => ({ type: UPDATE_EMPLOYEE_ERROR, employee, error });

export const updateEmployeeEpic = (
    action$: Observable<UpdateEmployeeAction>,
    store$: StateObservable<AppState>
) =>
    action$.pipe(
        ofType( UPDATE_EMPLOYEE ),
        switchMap( action => {
            if( action.employee.id === null ) {
                throw Error("user id cannot be null!");
            }

            const path = `${APP_CONFIG.baseWeb}employees/${ action.employee.id }`;
            const language = currentLanguageFromStore( store$ );
            let daysOfWork = {};

            if( action.employee.contract !== null ) {

                for( const index in action.employee.contract.daysOfWork ) {

                    const dayOfWork = action.employee.contract.daysOfWork[index];

                    daysOfWork = {
                        ...daysOfWork,
                        [ index ]: {
                            ...dayOfWork,
                            start: nullableDateAsTimestamp( dayOfWork.start ),
                            end: nullableDateAsTimestamp( dayOfWork.end ),
                            //dayOfWeek: Weekday[ action.employee.contract?.daysOfWork[ index ].dayOfWeek ] ?? 0
                        }
                    }
                }
            }


            return ajax( APP_CONFIG.getRequestObject( path, RouteMethods.put, {
                ...action.employee,
                birthday: dateAsTimestamp(  action.employee.birthday ),
                contract: {
                    ...action.employee.contract,
                    id: action.employee.contract?.employeeContractId,
                    start: nullableDateAsTimestamp( action.employee.contract?.start ?? null ),
                    end: nullableDateAsTimestamp( action.employee.contract?.end ?? null),
                    daysOfWork: daysOfWork
                },
                qualifications: action.employee.qualifications?.map( quali => {
                    return {
                        ...quali,
                        lastRefresh: nullableDateAsTimestamp( quali.lastRefresh ),
                        nextRefresh: nullableDateAsTimestamp( quali.nextRefresh )
                    };
                }) ?? []
            }) ).pipe(
                mergeMap( response => of(
                    updateEmployeeSuccess( response.response as Employee ),
                    eventNotificationShow( EVENT_NOTIFICATION_TYPE.ajaxSuccess ),
                    fetchEmployee( action.employee.id! ),
                    fetchUserGroupsByUser( action.employee.id! ),
                    sendUserToPage( RouteConfig.employees.routes.detail.link!( action.employee.id ) , EmployeeModule.identifier )
                )),
                catchError( error => of(
                    updateEmployeeError( action.employee, error ),
                    eventNotificationShow( EVENT_NOTIFICATION_TYPE.customAjaxError, `Mitarbeiter konnte nicht gespeichert werden.` )
                ) )
            );
        })
    );


/**
 * CHECK IF EMAIL ALREADY EXISTS
 *
 * **/
export interface CheckEmailUniquenessAction {
    type: typeof CHECK_EMAIL_UNIQUENESS
    mail: string
}

export interface CheckEmailUniquenessSuccessAction {
    type: typeof CHECK_EMAIL_UNIQUENESS_SUCCESS
    mailCount: number
    mail: string
}

export interface CheckEmailUniquenessErrorAction {
    type: typeof CHECK_EMAIL_UNIQUENESS_ERROR
    error: Error
}
export type CheckEmailUniquenessActionTypes = CheckEmailUniquenessAction | CheckEmailUniquenessErrorAction | CheckEmailUniquenessSuccessAction

export const CHECK_EMAIL_UNIQUENESS = "CHECK_EMAIL_UNIQUENESS";
export const CHECK_EMAIL_UNIQUENESS_SUCCESS = "CHECK_EMAIL_UNIQUENESS_SUCCESS";
export const CHECK_EMAIL_UNIQUENESS_ERROR = "CHECK_EMAIL_UNIQUENESS_ERROR";

export const checkEmailUniqueness = ( mail: string ): CheckEmailUniquenessAction => ({ type: CHECK_EMAIL_UNIQUENESS, mail });
export const checkEmailUniquenessSuccess = ( mail: string, mailCount: number ): CheckEmailUniquenessSuccessAction => ({ type: CHECK_EMAIL_UNIQUENESS_SUCCESS, mailCount, mail });
export const checkEmailUniquenessError = ( error: Error ): CheckEmailUniquenessErrorAction => ( { type: CHECK_EMAIL_UNIQUENESS_ERROR, error });

export const checkEmailUniquenessEpic = ( action$: Observable<CheckEmailUniquenessAction> ) =>
    action$.pipe(
        ofType( CHECK_EMAIL_UNIQUENESS ),
        switchMap( action => {

            const path = `${APP_CONFIG.baseWeb}employees/check/${action.mail}`;

            return ajax( APP_CONFIG.getRequestObject(path) )
                .pipe(
                    map( response => checkEmailUniquenessSuccess( action.mail, response.response as number ) ),
                    catchError( error => of( checkEmailUniquenessError( error ) ) )
                )

        })
    );